import React, { useState } from "react";
import { toast } from "sonner";
import { DatabaseBackup } from "lucide-react";
import api from "../../lib/api";

const fmtBytes = (b) => (b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1e3))} KB`);

// Create a full database export on the server, then download it once (the server deletes it afterwards).
export function BackupPanel() {
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  const run = async () => {
    setBusy(true);
    setStatus({ kind: "info", text: "Creating backup…" });
    try {
      const { data } = await api.post("/admin/backup");
      setStatus({ kind: "info", text: `Downloading ${fmtBytes(data.bytes)}…` });
      const file = await api.get(`/admin/backup/${data.backup_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(file.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${data.backup_id}.jsonl.gz`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      const msg = `${data.collections} collections, ${data.total_docs.toLocaleString()} documents, ${fmtBytes(data.bytes)}`;
      if (data.complete) {
        setStatus({ kind: "ok", text: `Backup complete: ${msg}` });
        toast.success("Backup downloaded");
      } else {
        setStatus({ kind: "warn", text: `Backup downloaded but some collections were short: ${Object.keys(data.short_collections).join(", ")}` });
        toast.warning("Backup may be incomplete");
      }
      window.dispatchEvent(new CustomEvent("admin-log-refresh"));
    } catch (e) {
      let detail = e.response?.data?.detail;
      if (e.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text()).detail; } catch { /* ignore */ }
      }
      setStatus({ kind: "err", text: `Backup failed: ${typeof detail === "string" ? detail : "unknown error"}` });
      toast.error("Backup failed");
    } finally {
      setBusy(false);
    }
  };

  const color = { ok: "#39FF14", warn: "#FFE600", err: "#FF007F", info: "#00F0FF" }[status?.kind] || "#A89BCC";
  return (
    <div className="rounded-xl border border-[#39194D] bg-[#12082B] p-4 space-y-3" data-testid="admin-backup-panel">
      <div className="font-heading text-sm neon-cyan flex items-center gap-2"><DatabaseBackup size={16} /> Database backup</div>
      <div className="font-mono text-xs text-[#A89BCC]">
        Exports every collection of the live database (read-only) into one restore-ready file. The file is deleted from the server after download.
      </div>
      <button data-testid="admin-backup-btn" disabled={busy} onClick={run}
        className="px-3 py-1.5 rounded-lg border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF]/15 font-heading text-xs uppercase tracking-widest disabled:opacity-50">
        {busy ? "Working…" : "Create & download backup"}
      </button>
      {status && <div data-testid="admin-backup-status" className="font-mono text-xs" style={{ color }}>{status.text}</div>}
    </div>
  );
}
